import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { coverageLabel } from '@ww/shared';
import { useAlertSound } from '../hooks/useAlertSound';
import { useLive } from '../hooks/useLive';
import { formatSilenceRemaining } from '../lib/soundPrefs';
import { I, Icon } from '../lib/icons';
import { ROLE_LABELS, initials, isAdmin } from '../lib/status';
import { DISCLAIMER, LiveClock, Logo, PollingPill, SystemBadge } from './HeaderBits';

function TenantSwitcher() {
  // The list comes from the live context so a tenant created on the admin page
  // appears here immediately, rather than after a full page reload.
  const { me, tenant, actingTenantId, setActingTenantId, tenants } = useLive();
  const isSuper = me?.user.role === 'super_admin';

  // The chip names the area being watched, not the tenant's own name — coverage
  // can be a city or a drawn polygon that has nothing to do with the org name.
  const label = tenant ? coverageLabel(tenant) : isSuper ? 'Select tenant' : 'No tenant';

  if (!isSuper || !tenants || tenants.length === 0) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          height: 40,
          padding: '0 12px',
          borderRadius: 8,
          border: '1px solid #26364f',
          background: '#0f1b30',
          flexShrink: 0,
          minWidth: 0,
        }}
      >
        <Icon d={I.pin} size={16} style={{ color: '#9fb0c8' }} />
        <span style={{ fontSize: 15, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {label}
        </span>
      </div>
    );
  }

  const value = actingTenantId ?? tenant?._id ?? '';
  return (
    <label style={{ position: 'relative', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
      <span className="sr-only">Acting tenant</span>
      <Icon d={I.pin} size={16} style={{ color: '#9fb0c8', position: 'absolute', left: 11, pointerEvents: 'none' }} />
      <select
        className="tenant-select hov"
        value={value}
        onChange={(e) => setActingTenantId(e.target.value || null)}
      >
        {!value && <option value="">Select tenant</option>}
        {tenants.map((t) => (
          <option key={t._id} value={t._id}>
            {t.name}
            {t.status === 'disabled' ? ' (disabled)' : ''}
          </option>
        ))}
      </select>
      <Icon
        d={I.chevD}
        size={14}
        style={{ color: '#9fb0c8', position: 'absolute', right: 10, pointerEvents: 'none' }}
      />
    </label>
  );
}

function UserChip() {
  const { me, logout } = useLive();
  const navigate = useNavigate();
  if (!me) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
      <span
        title={me.user.name}
        style={{
          width: 34,
          height: 34,
          borderRadius: '50%',
          background: '#1d3358',
          color: '#cfe1ff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 13,
          fontWeight: 700,
        }}
      >
        {initials(me.user.name || me.user.username)}
      </span>
      <span className="hide-md" style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
        <span style={{ fontSize: 13.5, color: '#e6edf7', fontWeight: 600 }}>{me.user.name || me.user.username}</span>
        <span style={{ fontSize: 12, color: '#b7c4d8' }}>{ROLE_LABELS[me.user.role] ?? me.user.role}</span>
      </span>
      <button
        type="button"
        className="hov"
        aria-label="Sign out"
        title="Sign out"
        onClick={() => {
          void logout().then(() => navigate('/login', { replace: true }));
        }}
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          border: '1px solid #26364f',
          background: 'transparent',
          color: '#9fb0c8',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon d={I.logout} size={18} />
      </button>
    </div>
  );
}

/**
 * Whether this machine is making a noise, and a one-tap mute.
 *
 * In the header rather than only on the settings page because both things it says are
 * urgent: an operator who needs silence now should not have to navigate for it, and an
 * operator who thinks they are covered needs to see that sound is blocked or muted
 * without going looking. Tap toggles mute; the page behind it has the timed options.
 */
function SoundChip() {
  const { silenced, prefs, silenceMsLeft, audio, enableAudio, setPrefs, unmute, policy } =
    useAlertSound();
  const navigate = useNavigate();

  if (audio === 'unsupported') return null;

  const blocked = audio === 'blocked';
  const title = blocked
    ? 'Sound is blocked by this browser — click to enable'
    : !policy.enabled
      ? 'Audible alerts are off for this county'
      : prefs.muted
        ? 'This device is muted — click to unmute'
        : silenceMsLeft > 0
          ? `Muted for another ${formatSilenceRemaining(silenceMsLeft)} — click to unmute`
          : 'Sound on — click to mute this device';

  const onClick = () => {
    if (blocked) {
      void enableAudio();
      return;
    }
    // The county switch is not ours to flip from a header chip; send them to the page.
    if (!policy.enabled) {
      navigate('/settings');
      return;
    }
    if (prefs.muted || silenceMsLeft > 0) unmute();
    else setPrefs({ muted: true, mutedUntil: null });
  };

  // A running snooze shows its remaining time. An icon alone cannot say "muted for
  // another 23 minutes", and that is the thing an operator actually needs to know
  // before they walk away from the desk.
  const countdown = !blocked && silenceMsLeft > 0 ? formatSilenceRemaining(silenceMsLeft) : null;
  const state = blocked ? 'blocked' : silenced ? 'muted' : 'on';

  return (
    <button
      type="button"
      className={`chip ${countdown ? '' : 'icon-chip '}sound-chip is-${state} hov`}
      onClick={onClick}
      aria-label={title}
      title={title}
      aria-pressed={silenced}
    >
      <Icon d={silenced ? I.muted : I.sound} size={16} />
      {countdown && <span className="sound-chip-time">{countdown}</span>}
    </button>
  );
}

/**
 * Shown while a super-admin has dropped in to a tenant's portal, so it is never in doubt
 * whose county is on screen. Actions still run (and are audited) as the super-admin.
 */
function DropInBanner() {
  const { me, tenant, actingTenantId, setActingTenantId } = useLive();
  const navigate = useNavigate();
  if (me?.user.role !== 'super_admin' || !actingTenantId || !tenant) return null;
  return (
    <div className="dropin-banner" role="status">
      <Icon d={I.users} size={16} />
      <span style={{ flexGrow: 1, minWidth: 0 }}>
        You are in <strong>{tenant.name}</strong>'s portal as a WeatherWatch administrator. Changes you make apply
        to them and are logged under your name.
      </span>
      <button
        type="button"
        className="btn btn-secondary btn-sm hov"
        onClick={() => navigate(`/admin/tenants/${tenant._id}`)}
      >
        Tenant settings
      </button>
      <button
        type="button"
        className="btn btn-primary btn-sm hov"
        onClick={() => {
          setActingTenantId(null);
          navigate('/admin');
        }}
      >
        Leave portal
      </button>
    </div>
  );
}

export default function Shell() {
  const { me, dashboard, health, tenant, connected } = useLive();
  const navigate = useNavigate();
  const location = useLocation();
  /*
   * On a phone the sidenav is a drawer. It is the same element and the same links —
   * only its position changes — because it also hosts #ww-sidebar-panels, and moving
   * the dashboard's arrival card and map tools to a different mount point on small
   * screens would mean two of everything to keep in step.
   */
  const [navOpen, setNavOpen] = useState(false);
  // Following a link must close it, or the drawer covers the page it just opened.
  useEffect(() => setNavOpen(false), [location.pathname]);
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);
  const counts = dashboard?.counts;
  const badge = counts ? counts.warning + counts.watch + counts.advisory : 0;
  // Allow-list — see the comment on RequireAdmin in App.tsx. This gates the Coverage
  // and Admin nav items, so a deny-list here hands a new role the whole admin surface.
  const isAdminUser = isAdmin(me?.user.role);

  const nav: { to: string; label: string; icon: string; badge?: number; end?: boolean }[] = [
    { to: '/', label: 'Dashboard', icon: I.dash, end: true },
    { to: '/alerts', label: 'Alerts', icon: I.bell, badge },
    { to: '/assets', label: 'Assets', icon: I.pin },
    { to: '/closures', label: 'Road closures', icon: I.cone },
    { to: '/rules', label: 'Alert rules', icon: I.rules },
    { to: '/health', label: 'System health', icon: I.health },
    { to: '/settings', label: 'Settings', icon: I.gear },
    { to: '/wallboard', label: 'Wallboard', icon: I.wall },
  ];
  if (isAdminUser) nav.push({ to: '/coverage', label: 'Coverage area', icon: I.target });
  if (isAdminUser) nav.push({ to: '/admin', label: 'Admin', icon: I.users });

  return (
    <div className="app">
      <header className="app-header">
        <button
          type="button"
          className="nav-toggle hov"
          aria-label={navOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={navOpen}
          aria-controls="ww-sidenav"
          onClick={() => setNavOpen((v) => !v)}
        >
          <Icon d={navOpen ? I.x : I.menu} size={22} />
        </button>
        <div className="header-logo">
          <Logo />
        </div>
        <TenantSwitcher />
        <span className="hide-sm" style={{ display: 'contents' }}>
          <PollingPill health={health} />
        </span>
        {/*
          The disclaimer lives here rather than in its own strip below the header, which
          cost ~26px of map height. It takes the space the spacer used to occupy, so
          nothing is pushed aside; `min-width: 0` lets it ellipsize instead of shoving
          the clock and user chip off the right edge.

          The hover text is a real element rather than a `title` attribute: native
          tooltips are drawn by the operating system and cannot be styled, and this one
          needs to read as a warning. `tabIndex` so it is reachable without a mouse.
          The tooltip itself is aria-hidden — the full wording is already in the DOM
          above it, so a screen reader would otherwise hear it twice.
        */}
        <div className="disclaimer-inline" role="note" tabIndex={0}>
          <span className="disclaimer-text">{DISCLAIMER}</span>
          <span className="disclaimer-tip" aria-hidden="true">
            {DISCLAIMER}
          </span>
        </div>
        {/* Page-owned header buttons portal in here; empty on every page but the dashboard. */}
        <div id="ww-header-actions" style={{ display: 'contents' }} />
        <SoundChip />
        <SystemBadge health={health} connected={connected} />
        <span className="hide-sm" style={{ display: 'contents' }}>
          <LiveClock tz={tenant?.timezone} />
        </span>
        <button
          type="button"
          className="btn btn-secondary hov hide-md"
          onClick={() => navigate('/wallboard')}
          style={{ padding: '0 14px' }}
        >
          <Icon d={I.wall} size={18} />
          <span>Wallboard</span>
        </button>
        <UserChip />
      </header>
      {/*
        Below ~1100px the header genuinely cannot hold this at a readable width, and a
        notice shrunk to three characters is worse than one on its own line. At those
        widths the strip comes back — it costs nothing there, because the layout has
        already switched to a scrolling column and the map is no longer height-bound.
      */}
      <div className="disclaimer disclaimer-strip" role="note">
        {DISCLAIMER}
      </div>
      <DropInBanner />
      <div className="app-body">
        {/* Only rendered while the drawer is open, so it cannot swallow taps otherwise. */}
        {navOpen && <div className="nav-scrim" onClick={() => setNavOpen(false)} aria-hidden="true" />}
        <nav id="ww-sidenav" aria-label="Main" className={navOpen ? 'sidenav open' : 'sidenav'}>
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => (isActive ? 'navlink active' : 'navlink')}>
              <Icon d={n.icon} size={20} />
              <span style={{ flexGrow: 1 }}>{n.label}</span>
              {n.badge ? (
                <span className="nav-badge" aria-label={`${n.badge} active alerts`}>
                  {n.badge}
                </span>
              ) : null}
            </NavLink>
          ))}
          {/*
            Mount point for page-owned sidebar panels (the dashboard's arrival card and
            map tools portal into here). Keeping them in the nav rather than floating
            over the map means they never cover the legend or the radar scrubber.
          */}
          <div id="ww-sidebar-panels" style={{ display: 'contents' }} />
          <div style={{ flexGrow: 1 }} />
          {tenant && (
            <p
              style={{
                margin: 0,
                padding: '12px 6px 0',
                borderTop: '1px solid #1f2d47',
                fontSize: 12.5,
                lineHeight: 1.45,
                color: '#93a5c1',
              }}
            >
              {`${coverageLabel(tenant)}${tenant.state ? `, ${tenant.state}` : ''}`}
              <br />
              <span className="mono" style={{ fontSize: 11.5 }}>
                {tenant.timezone}
              </span>
              <br />
              <span className="mono" style={{ fontSize: 11.5, opacity: 0.75 }} title="WeatherWatch build version">
                v{__APP_VERSION__}
              </span>
            </p>
          )}
        </nav>
        <main className="app-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
